import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import 'moment/locale/pt-br';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import api from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CalendarIcon, Plus, X, Edit2, Trash2, BookOpen, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import './CalendarView.css';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

moment.locale('pt-br');
const localizer = momentLocalizer(moment);
const DragAndDropCalendar = withDragAndDrop(Calendar);

// Função para calcular gaps livres dinamicamente
const calculateFreeSlots = (events, date) => {
  const dayStart = 6 * 60; // 06:00
  const dayEnd = 23 * 60; // 23:00
  
  // Filtrar eventos do dia específico
  const dateStr = moment(date).format('YYYY-MM-DD');
  const dayEvents = events.filter(e => {
    const eventDate = moment(e.start).format('YYYY-MM-DD');
    return eventDate === dateStr && e.type !== 'free';
  });
  
  // Converter eventos para minutos
  const busy = dayEvents.map(e => {
    const start = e.start.getHours() * 60 + e.start.getMinutes();
    const end = e.end.getHours() * 60 + e.end.getMinutes();
    return { start, end };
  }).sort((a, b) => a.start - b.start);
  
  // Calcular gaps
  const gaps = [];
  let current = dayStart;
  
  for (const slot of busy) {
    if (slot.start > current) {
      gaps.push({ start: current, end: slot.start });
    }
    current = Math.max(current, slot.end);
  }
  
  if (dayEnd > current) {
    gaps.push({ start: current, end: dayEnd });
  }
  
  return gaps;
};

// Formatar duração
const formatDuration = (minutes) => {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) {
      return `${hours} hora${hours > 1 ? 's' : ''}`;
    }
    return `${hours}h ${mins}min`;
  }
  return `${minutes} min`;
};

export default function CalendarView() {
  const [events, setEvents] = useState([]);
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newEventTitle, setNewEventTitle] = useState('');
  const [editEventTitle, setEditEventTitle] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchTopics();
    fetchRoutines();
  }, []);

  // Ref para evitar loop infinito na geração de free slots
  const isGeneratingFreeSlots = useRef(false);

  // Calcular e adicionar gaps livres quando eventos mudam
  useEffect(() => {
    // Evitar loop infinito - só gera free slots se não estiver em processo de geração
    if ((view === 'week' || view === 'day') && !isGeneratingFreeSlots.current) {
      // Marcar que está gerando para evitar re-trigger
      isGeneratingFreeSlots.current = true;
      generateFreeSlots();
      // Limpar flag após um pequeno delay para permitir futuras atualizações
      setTimeout(() => {
        isGeneratingFreeSlots.current = false;
      }, 100);
    }
  }, [view, currentDate]); // Removido 'events' da dependência para evitar loop

  // Regenerar free slots quando eventos de estudo/revisão/rotina mudam (não free slots)
  const nonFreeEventsCount = events.filter(e => e.type !== 'free').length;
  useEffect(() => {
    if ((view === 'week' || view === 'day') && !isGeneratingFreeSlots.current) {
      isGeneratingFreeSlots.current = true;
      generateFreeSlots();
      setTimeout(() => {
        isGeneratingFreeSlots.current = false;
      }, 100);
    }
  }, [nonFreeEventsCount]); // Só re-gera quando quantidade de eventos não-free muda

  const generateFreeSlots = () => {
    const freeSlots = [];
    const daysToCheck = view === 'day' ? 1 : 7;
    const startDate = view === 'day' ? currentDate : moment(currentDate).startOf('week').toDate();
    
    for (let i = 0; i < daysToCheck; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const gaps = calculateFreeSlots(events.filter(e => e.type !== 'free'), date);
      
      gaps.forEach((gap, index) => {
        if (gap.end - gap.start >= 30) { // Mínimo 30 minutos
          const startDate = new Date(date);
          startDate.setHours(Math.floor(gap.start / 60), gap.start % 60, 0, 0);
          
          const endDate = new Date(date);
          endDate.setHours(Math.floor(gap.end / 60), gap.end % 60, 0, 0);
          
          const duration = gap.end - gap.start;
          
          freeSlots.push({
            id: `free-${date.toISOString().split('T')[0]}-${index}`,
            title: `Livre - ${formatDuration(duration)}`,
            start: startDate,
            end: endDate,
            allDay: false,
            type: 'free',
            duration: duration,
            isDraggable: false
          });
        }
      });
    }
    
    // Atualizar eventos removendo gaps antigos e adicionando novos
    setEvents(prev => {
      const nonFreeEvents = prev.filter(e => e.type !== 'free');
      return [...nonFreeEvents, ...freeSlots];
    });
  };

  const fetchRoutines = async () => {
    try {
      const response = await api.get('/schedules');
      const schedules = response.data.schedules || [];
      
      // Gerar eventos de rotina para os próximos 60 dias
      const routineEvents = [];
      const today = new Date();
      
      // JavaScript: 0=Domingo, 1=Segunda... 6=Sábado
      // Python weekday(): 0=Segunda, 1=Terça... 6=Domingo
      const dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
      // Map do Python weekday para JS getDay
      const pythonToJs = {0: 1, 1: 2, 2: 3, 3: 4, 4: 5, 5: 6, 6: 0}; // Python -> JS
      
      for (let i = 0; i < 60; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        const jsWeekday = date.getDay(); // JS: 0=Domingo
        const dayName = dayNames[jsWeekday];
        const dateStr = date.toISOString().split('T')[0];
        
        schedules.forEach(schedule => {
          let shouldShow = false;
          const daysOfWeek = schedule.days_of_week;
          
          // Suportar formato numérico [0,1,2] (Python weekday) ou string "Segunda,Terça"
          if (Array.isArray(daysOfWeek)) {
            // Converter Python weekday para JS e verificar
            const jsDays = daysOfWeek.map(d => pythonToJs[d]);
            shouldShow = jsDays.includes(jsWeekday);
          } else if (typeof daysOfWeek === 'string') {
            const days = daysOfWeek.split(',').map(d => d.trim());
            shouldShow = days.includes(dayName);
          }
          
          if (shouldShow) {
            // Calcular horário de início e fim
            const [startH, startM] = (schedule.start_time || '09:00').split(':').map(Number);
            const [endH, endM] = (schedule.end_time || '10:00').split(':').map(Number);
            
            const startDate = new Date(date);
            startDate.setHours(startH, startM, 0, 0);
            
            const endDate = new Date(date);
            endDate.setHours(endH, endM, 0, 0);
            
            routineEvents.push({
              id: `routine-${schedule.id}-${dateStr}`,
              title: `🕐 ${schedule.title}`,
              start: startDate,
              end: endDate,
              allDay: false,
              type: 'routine',
              resource: schedule,
              isDraggable: false
            });
          }
        });
      }
      
      setEvents(prev => {
        // Remover rotinas antigas e adicionar novas
        const nonRoutineEvents = prev.filter(e => e.type !== 'routine');
        return [...nonRoutineEvents, ...routineEvents];
      });
    } catch (error) {
      console.error('Error fetching routines:', error);
    }
  };

  const fetchTopics = async () => {
    try {
      const response = await api.get('/topics');
      const topics = response.data;
      
      const calendarEvents = [];
      
      topics.forEach(topic => {
        // Parse da data sem problemas de timezone
        const dateParts = topic.scheduled_date.split('T')[0].split('-');
        const date = new Date(
          parseInt(dateParts[0]),
          parseInt(dateParts[1]) - 1,
          parseInt(dateParts[2]),
          9, 0, 0 // Hora padrão 9:00
        );
        
        if (topic.scheduled_time) {
          const [hours, minutes] = topic.scheduled_time.split(':');
          date.setHours(parseInt(hours), parseInt(minutes));
        }
        
        // Calcular status do estudo
        const completedStages = [topic.reading_done, topic.deep_dive_done, topic.questions_done].filter(Boolean).length;
        let studyStatus = 'not_started'; // não iniciado
        if (completedStages > 0 && completedStages < 3) {
          studyStatus = 'in_progress'; // em progresso
        } else if (completedStages === 3) {
          studyStatus = 'completed'; // completo
        }
        
        // Adicionar indicador ao título se em progresso
        let displayTitle = topic.title;
        if (studyStatus === 'in_progress') {
          displayTitle = `⏳ ${topic.title}`;
        } else if (studyStatus === 'completed') {
          displayTitle = `✓ ${topic.title}`;
        }
        
        calendarEvents.push({
          id: topic.id,
          title: displayTitle,
          start: date,
          end: new Date(date.getTime() + 60 * 60 * 1000),
          allDay: true,
          type: 'study',
          studyStatus: studyStatus,
          completedStages: completedStages,
          specialty: topic.specialty,
          resource: topic,
          isDraggable: true
        });
        
        if (topic.review_dates && topic.review_dates.length > 0) {
          const intervals = ['24h', '7d', '15d', '30d'];
          topic.review_dates.forEach((reviewDate, index) => {
            const revDateParts = reviewDate.split('T')[0].split('-');
            const revDate = new Date(
              parseInt(revDateParts[0]),
              parseInt(revDateParts[1]) - 1,
              parseInt(revDateParts[2]),
              9, 0, 0
            );
            
            if (topic.scheduled_time) {
              const [hours, minutes] = topic.scheduled_time.split(':');
              revDate.setHours(parseInt(hours), parseInt(minutes));
            }
            
            calendarEvents.push({
              id: `${topic.id}-review-${index}`,
              title: `R${index + 1}: ${topic.title}`,
              start: revDate,
              end: new Date(revDate.getTime() + 60 * 60 * 1000),
              allDay: true,
              type: 'review',
              reviewIndex: index,
              interval: intervals[index],
              resource: topic,
              isDraggable: true,
              isCompleted: topic.completed_reviews?.includes(index) || false
            });
          });
        }
      });
      
      setEvents(prev => {
        // Manter rotinas e substituir estudos/revisões
        const routineEvents = prev.filter(e => e.type === 'routine');
        return [...routineEvents, ...calendarEvents];
      });
    } catch (error) {
      console.error('Error fetching topics:', error);
      toast.error('Erro ao carregar calendário');
    }
  };

  const eventStyleGetter = (event) => {
    let backgroundColor = '#3b82f6'; // Azul padrão - não iniciado
    let cursor = 'pointer';
    let borderLeft = 'none';
    let opacity = 0.9;
    
    if (event.type === 'free') {
      // Gap livre - verde claro
      backgroundColor = '#10b981';
      cursor = 'default';
      opacity = 0.6;
      borderLeft = '4px solid #059669';
    } else if (event.type === 'routine') {
      // Rotina - cinza neutro
      backgroundColor = '#6b7280';
      cursor = 'default';
      opacity = 0.7;
      borderLeft = '4px solid #4b5563';
    } else if (event.type === 'review') {
      // Revisão concluída - verde
      if (event.isCompleted) {
        backgroundColor = '#22c55e';
      } else {
        if (event.interval === '24h') backgroundColor = '#eab308';
        else if (event.interval === '7d') backgroundColor = '#f97316';
        else if (event.interval === '15d') backgroundColor = '#ef4444';
        else backgroundColor = '#dc2626';
      }
      cursor = 'grab';
    } else {
      // Cores baseadas no status do estudo
      if (event.studyStatus === 'in_progress') {
        backgroundColor = '#f59e0b'; // Âmbar - em progresso
        borderLeft = '4px solid #d97706';
      } else if (event.studyStatus === 'completed') {
        backgroundColor = '#22c55e'; // Verde - completo
      }
    }
    
    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        opacity,
        color: 'white',
        border: 'none',
        borderLeft,
        display: 'block',
        fontSize: '12px',
        fontWeight: '500',
        cursor
      }
    };
  };

  const handleSelectEvent = (event) => {
    // Não abrir modal para gaps livres
    if (event.type === 'free') {
      return;
    }
    // Abrir modal de edição para outros eventos
    setSelectedEvent(event);
    setEditEventTitle(event.title.replace(/^(⏳ |✓ |R\d+: |🕐 )/, '')); // Remove prefixos
    setShowEditDialog(true);
  };

  const handleSelectSlot = (slotInfo) => {
    setSelectedDate(slotInfo.start);
    setNewEventTitle('');
    setShowAddDialog(true);
  };

  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  const handleAddEvent = async () => {
    if (!newEventTitle.trim()) {
      toast.error('Digite um título para o evento');
      return;
    }

    try {
      const dateStr = moment(selectedDate).format('YYYY-MM-DD');
      await api.post('/calendar/events', {
        title: newEventTitle,
        date: dateStr,
        event_type: 'study'
      });
      
      toast.success('Evento adicionado!');
      setShowAddDialog(false);
      setNewEventTitle('');
      fetchTopics();
    } catch (error) {
      console.error('Error adding event:', error);
      toast.error('Erro ao adicionar evento');
    }
  };

  const handleUpdateEvent = async () => {
    if (!editEventTitle.trim() || !selectedEvent) {
      toast.error('Digite um título para o evento');
      return;
    }

    // Fechar modal imediatamente
    setShowEditDialog(false);
    
    // Atualização otimista - atualizar título na lista local
    const eventId = selectedEvent.id;
    setEvents(prev => prev.map(e => {
      if (e.id === eventId) {
        return { ...e, title: editEventTitle };
      }
      return e;
    }));

    try {
      await api.patch(`/calendar/events/${eventId}`, {
        title: editEventTitle
      });
      
      toast.success('Evento atualizado!');
      await fetchTopics();
      await fetchRoutines();
    } catch (error) {
      console.error('Error updating event:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao atualizar evento';
      toast.error(errorMsg);
      await fetchTopics();
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    // Fechar modal imediatamente
    setShowEditDialog(false);
    
    // Atualização otimista - remover da lista local IMEDIATAMENTE
    const eventId = selectedEvent.id;
    setEvents(prev => prev.filter(e => e.id !== eventId));

    try {
      await api.delete(`/calendar/events/${eventId}`);
      
      if (selectedEvent.type === 'review') {
        toast.success('Revisão removida/ignorada!');
      } else {
        toast.success('Evento removido!');
      }
      
      await fetchTopics();
      await fetchRoutines();
    } catch (error) {
      console.error('Error deleting event:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao remover evento';
      toast.error(errorMsg);
      await fetchTopics();
    }
  };

  const handleCompleteEvent = async () => {
    if (!selectedEvent) return;

    // Fechar modal imediatamente
    setShowEditDialog(false);
    
    // Atualização otimista - marcar evento como concluído na lista local
    const eventId = selectedEvent.id;
    setEvents(prev => prev.map(e => {
      if (e.id === eventId) {
        if (e.type === 'review') {
          return { ...e, isCompleted: true };
        } else {
          return { ...e, studyStatus: 'completed' };
        }
      }
      return e;
    }));

    try {
      await api.post(`/calendar/events/${eventId}/complete`);
      
      if (selectedEvent.type === 'review') {
        toast.success('Revisão marcada como concluída!');
      } else {
        toast.success('Estudo marcado como concluído!');
      }
      
      // Re-fetch para garantir sincronização
      await fetchTopics();
      await fetchRoutines();
    } catch (error) {
      console.error('Error completing event:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao concluir evento';
      toast.error(errorMsg);
      // Em caso de erro, re-fetch para restaurar estado correto
      await fetchTopics();
      await fetchRoutines();
    }
  };

  // Ações rápidas para revisões com atualização imediata (Optimistic UI)
  const handleReviewAction = async (action) => {
    if (!selectedEvent || selectedEvent.type !== 'review') return;
    
    const topicId = selectedEvent.resource?.id;
    const reviewIndex = selectedEvent.reviewIndex;
    const eventId = selectedEvent.id;
    
    if (!topicId || reviewIndex === undefined) {
      toast.error('Erro ao identificar revisão');
      return;
    }

    // PASSO 1: Fechar modal IMEDIATAMENTE
    setShowEditDialog(false);
    setSelectedEvent(null);
    
    // PASSO 2: Atualização otimista IMEDIATA - remover evento da lista local ANTES da API
    setEvents(prevEvents => {
      const filtered = prevEvents.filter(e => e.id !== eventId);
      console.log('[Optimistic UI] Removendo evento do calendário:', { eventId, prevCount: prevEvents.length, newCount: filtered.length });
      return filtered;
    });

    // PASSO 3: Fazer chamada API em background (não bloqueia UI)
    try {
      const response = await api.post(`/topics/${topicId}/review-action`, {
        review_index: reviewIndex,
        action: action
      });
      toast.success(response.data.message);
      
      // PASSO 4: Re-fetch com delay para sincronização (não sobrescreve UI imediatamente)
      setTimeout(() => {
        fetchTopics();
        fetchRoutines();
      }, 500);
    } catch (error) {
      console.error('Error performing review action:', error);
      toast.error('Erro ao executar ação. Recarregando...');
      // Em caso de erro, re-fetch para restaurar estado correto
      fetchTopics();
      fetchRoutines();
    }
  };

  const handleEventDrop = useCallback(async ({ event, start, end }) => {
    // Calcular a nova data corretamente
    const newStart = new Date(start);
    const newEnd = end ? new Date(end) : new Date(newStart.getTime() + 60 * 60 * 1000);

    // Atualização otimista - atualiza UI imediatamente
    const updatedEvents = events.map(e => {
      if (e.id === event.id) {
        return {
          ...e,
          start: newStart,
          end: newEnd
        };
      }
      return e;
    });
    setEvents(updatedEvents);

    try {
      // Usar a data local corretamente
      const year = newStart.getFullYear();
      const month = String(newStart.getMonth() + 1).padStart(2, '0');
      const day = String(newStart.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      if (event.type === 'review') {
        // Mover revisão
        const topicId = event.resource.id;
        const reviewIndex = event.reviewIndex;
        
        await api.patch(`/topics/${topicId}/review-date`, {
          review_index: reviewIndex,
          new_date: dateStr
        });
        
        toast.success(`Revisão movida para ${day}/${month}/${year}`);
      } else {
        // Mover estudo
        await api.patch(`/calendar/events/${event.id}`, {
          date: dateStr
        });
        
        toast.success(`Evento movido para ${day}/${month}/${year}`);
      }
    } catch (error) {
      console.error('Error moving event:', error);
      toast.error('Erro ao mover evento');
      // Reverter em caso de erro
      fetchTopics();
    }
  }, [events]);

  const messages = {
    allDay: 'Dia inteiro',
    previous: 'Anterior',
    next: 'Próximo',
    today: 'Hoje',
    month: 'Mês',
    week: 'Semana',
    day: 'Dia',
    agenda: 'Agenda',
    date: 'Data',
    time: 'Hora',
    event: 'Evento',
    noEventsInRange: 'Nenhum evento neste período',
    showMore: (total) => `+ ${total} mais`
  };

  return (
    <div className="page-container" data-testid="calendar-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CalendarIcon className="w-8 h-8 text-blue-600" />
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Calendário</h1>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant={view === 'week' ? 'default' : 'outline'}
              onClick={() => setView('week')}
              data-testid="view-week"
            >
              Semana
            </Button>
            <Button
              variant={view === 'month' ? 'default' : 'outline'}
              onClick={() => setView('month')}
              data-testid="view-month"
            >
              Mês
            </Button>
          </div>
        </div>

        <Card className="p-6 shadow-lg border-2 border-blue-200 bg-white dark:bg-slate-900">
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Clique em um dia para adicionar evento | Arraste para reagendar | Clique em um evento para editar
          </p>
          <div className="calendar-wrapper">
            <DragAndDropCalendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 600 }}
              view={view}
              onView={setView}
              date={currentDate}
              onNavigate={handleNavigate}
              eventPropGetter={eventStyleGetter}
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              onEventDrop={handleEventDrop}
              selectable
              resizable={false}
              draggableAccessor={(event) => event.isDraggable}
              messages={messages}
              popup
            />
          </div>
        </Card>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-600"></div>
            <span className="text-sm text-slate-700 dark:text-slate-300">Não iniciado</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-amber-500 border-l-4 border-amber-600"></div>
            <span className="text-sm text-slate-700 dark:text-slate-300">⏳ Em progresso</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500"></div>
            <span className="text-sm text-slate-700 dark:text-slate-300">✓ Completo</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-yellow-500"></div>
            <span className="text-sm text-slate-700 dark:text-slate-300">R1 (24h)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-orange-500"></div>
            <span className="text-sm text-slate-700 dark:text-slate-300">R2 (7d)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-500"></div>
            <span className="text-sm text-slate-700 dark:text-slate-300">R3 (15d)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-600"></div>
            <span className="text-sm text-slate-700 dark:text-slate-300">R4 (30d)</span>
          </div>
        </div>
      </div>

      {/* Dialog para adicionar evento */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Adicionar Estudo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                Data: {selectedDate && moment(selectedDate).format('DD/MM/YYYY')}
              </p>
              <Input
                placeholder="Título do tópico de estudo"
                value={newEventTitle}
                onChange={(e) => setNewEventTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddEvent()}
                data-testid="new-event-title"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddEvent} data-testid="confirm-add-event">
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para editar/remover evento */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5" />
              {selectedEvent?.type === 'routine' ? 'Atividade de Rotina' : 
               selectedEvent?.type === 'review' ? 'Detalhes da Revisão' : 'Editar Evento'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Mostrar informações do evento */}
            <div className="space-y-2">
              {selectedEvent?.type === 'routine' ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    <strong>Atividade:</strong> {selectedEvent?.resource?.title}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <strong>Horário:</strong> {selectedEvent?.resource?.start_time} - {selectedEvent?.resource?.end_time}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <strong>Data:</strong> {selectedEvent?.start && moment(selectedEvent.start).format('DD/MM/YYYY')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2 p-2 bg-muted rounded">
                    💡 Para editar ou remover rotinas, acesse "Meu Cronograma" no menu.
                  </p>
                </>
              ) : selectedEvent?.type === 'review' ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    <strong>Revisão:</strong> {selectedEvent?.interval}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <strong>Tema:</strong> {selectedEvent?.resource?.title}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <strong>Data:</strong> {selectedEvent?.start && moment(selectedEvent.start).format('DD/MM/YYYY')}
                  </p>
                  {selectedEvent?.isCompleted && (
                    <p className="text-sm text-green-600 font-medium flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Revisão concluída
                    </p>
                  )}
                </>
              ) : (
                <Input
                  placeholder="Título do evento"
                  value={editEventTitle}
                  onChange={(e) => setEditEventTitle(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleUpdateEvent()}
                  data-testid="edit-event-title"
                  autoFocus
                />
              )}
            </div>
          </div>
          <DialogFooter className="flex flex-col gap-2">
            {/* Linha 1: Botões de ação principal */}
            <div className="flex flex-wrap gap-2 w-full">
              {/* Botão Concluir - apenas para estudos e revisões não concluídas */}
              {selectedEvent?.type !== 'routine' && selectedEvent?.type !== 'free' && !(selectedEvent?.studyStatus === 'completed' || selectedEvent?.isCompleted) && (
                <Button 
                  variant="default"
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={selectedEvent?.type === 'review' ? () => handleReviewAction('complete') : handleCompleteEvent}
                  data-testid="complete-event-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Concluir
                </Button>
              )}
              
              {/* Botão Estudar - apenas para estudos e revisões (não rotinas nem gaps) */}
              {selectedEvent?.type !== 'routine' && selectedEvent?.type !== 'free' && (
                <Button 
                  variant="default"
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    setShowEditDialog(false);
                    const topicId = selectedEvent?.resource?.id || selectedEvent?.id;
                    
                    if (!topicId || topicId.includes('-review-') || topicId.includes('routine-')) {
                      // Para revisões, usar o ID do resource
                      if (selectedEvent?.resource?.id) {
                        navigate(`/topics/${selectedEvent.resource.id}`);
                      } else {
                        toast.error('Não foi possível identificar o tema');
                      }
                      return;
                    }
                    
                    navigate(`/topics/${topicId}`);
                  }}
                  data-testid="study-event-btn"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Estudar
                </Button>
              )}
            </div>
            
            {/* Linha 2: Ações específicas para revisões */}
            {selectedEvent?.type === 'review' && !selectedEvent?.isCompleted && (
              <div className="flex flex-wrap gap-2 w-full">
                <Button 
                  variant="outline"
                  className="flex-1 text-blue-600 border-blue-300 hover:bg-blue-50"
                  onClick={() => handleReviewAction('postpone')}
                  data-testid="postpone-review-btn"
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Adiar +1 dia
                </Button>
                <Button 
                  variant="outline"
                  className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => handleReviewAction('skip')}
                  data-testid="skip-review-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Pular
                </Button>
              </div>
            )}
            
            {/* Linha 3: Botões secundários */}
            <div className="flex flex-wrap gap-2 w-full">
              {/* Remover - para estudos E revisões (não rotinas nem gaps) */}
              {(selectedEvent?.type === 'study' || selectedEvent?.type === 'review') && (
                <Button 
                  variant="destructive" 
                  onClick={handleDeleteEvent}
                  data-testid="delete-event-btn"
                  className="flex-1"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {selectedEvent?.type === 'review' ? 'Ignorar' : 'Remover'}
                </Button>
              )}
              
              <Button variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">
                {selectedEvent?.type === 'routine' || selectedEvent?.type === 'free' ? 'Fechar' : 'Cancelar'}
              </Button>
              
              {/* Salvar - para estudos E revisões (edição de data) */}
              {(selectedEvent?.type === 'study' || selectedEvent?.type === 'review') && (
                <Button onClick={handleUpdateEvent} data-testid="confirm-edit-event" className="flex-1">
                  Salvar
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
